/**
 * One axis per evaluator, 0–1, drawn as plain SVG so it scales with its card:
 * rings at 0.25 / 0.5 / 0.75 / 1 (unnumbered: each axis prints its own score),
 * a dot on each axis, and past its end the full name wrapped onto lines with
 * the score on the line nearest the chart, so no text sits on the rings or
 * dots. A tooltip (evaluator, score, pass rate) shows on hover.
 * Colours are theme tokens, so it reads in light and dark.
 *
 * With fewer than three evaluators a radar would be a line, so those show as bars.
 */

import { useState } from "react";
import { formatPercent, formatScore } from "@/lib/format";

export interface RadarPoint {
  name: string;
  value: number;
  pass?: number | null | undefined;
}

const W = 460;
const H = 380;
const CX = W / 2;
const CY = H / 2;
const R = 96;
const RINGS = [0.25, 0.5, 0.75, 1];
/** The SVG scales down with its card (~0.8×), so its type is set a size up. */
const LINE = 16;

function at(i: number, n: number, r: number): [number, number] {
  const angle = -Math.PI / 2 + (2 * Math.PI * i) / n;
  return [CX + r * Math.cos(angle), CY + r * Math.sin(angle)];
}

/** Words onto lines of about 14 characters, at most three lines. */
export function wrapLabel(name: string, width = 14, maxLines = 3): string[] {
  const lines: string[] = [];
  for (const word of name.split(/\s+/)) {
    const last = lines[lines.length - 1];
    if (last !== undefined && `${last} ${word}`.length <= width) lines[lines.length - 1] = `${last} ${word}`;
    else lines.push(word);
  }
  if (lines.length <= maxLines) return lines;
  const kept = lines.slice(0, maxLines);
  kept[maxLines - 1] = `${kept[maxLines - 1]}…`;
  return kept;
}

function AxisLabel({ i, n, point }: { i: number; n: number; point: RadarPoint }) {
  const [x, y] = at(i, n, R + 16);
  const above = y < CY - R * 0.5;
  const below = y > CY + R * 0.5;
  const name = wrapLabel(point.name).map((text) => ({ text, score: false }));
  const score = { text: formatScore(point.value), score: true };
  // The score is the line nearest the chart: last above it, first below it.
  const lines = above ? [...name, score] : [score, ...name];
  const anchor = Math.abs(x - CX) < 8 ? "middle" : x > CX ? "start" : "end";
  const extra = (lines.length - 1) * LINE;
  const startY = above ? y - extra : below ? y + 12 : y + 5 - extra / 2;
  return (
    <text textAnchor={anchor} className="fill-body text-[14px]" aria-hidden>
      {lines.map((line, k) => (
        <tspan
          key={k}
          x={x}
          y={startY + k * LINE}
          className={line.score ? "fill-ink font-mono text-[13px] font-semibold" : undefined}
        >
          {line.text}
        </tspan>
      ))}
    </text>
  );
}

/** One or two evaluators: a radar would be a line, so each score is a bar. */
function ScoreBars({ data }: { data: RadarPoint[] }) {
  return (
    <div className="flex flex-col gap-3 px-1 py-3">
      {data.map((d) => (
        <div key={d.name} className="flex flex-col gap-1 text-[13px]">
          <span className="flex justify-between gap-2">
            <span className="truncate" title={d.name}>
              {d.name}
            </span>
            <span className="font-mono">{formatScore(d.value)}</span>
          </span>
          <span className="h-1.5 overflow-hidden rounded-full bg-track">
            <span className="block h-1.5 rounded-full bg-link" style={{ width: `${d.value * 100}%` }} />
          </span>
        </div>
      ))}
      <p className="text-xs text-muted">The radar appears with three or more scored evaluators.</p>
    </div>
  );
}

export function ScoreRadar({ data }: { data: RadarPoint[] }) {
  const [hover, setHover] = useState<number | null>(null);
  if (data.length === 0) {
    return <p className="grid min-h-[180px] place-items-center text-[13px] text-muted">No scores yet.</p>;
  }
  if (data.length < 3) return <ScoreBars data={data} />;

  const n = data.length;
  const ring = (r: number) => data.map((_, i) => at(i, n, R * r).join(",")).join(" ");
  const points = data.map((d, i) => at(i, n, R * Math.max(0, Math.min(1, d.value))));
  const shown = hover === null ? null : { d: data[hover]!, p: points[hover]! };

  return (
    <div className="relative" onMouseLeave={() => setHover(null)}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`Radar chart of ${n} evaluator scores`}
        className="h-auto w-full overflow-visible"
      >
        {RINGS.map((r) => (
          <polygon
            key={r}
            points={ring(r)}
            fill={r === 1 ? "var(--color-well)" : "none"}
            stroke="var(--color-line)"
            strokeWidth={1}
          />
        ))}
        {data.map((_, i) => {
          const [x, y] = at(i, n, R);
          return <line key={i} x1={CX} y1={CY} x2={x} y2={y} stroke="var(--color-line-soft)" />;
        })}
        <polygon
          points={points.map((p) => p.join(",")).join(" ")}
          fill="var(--color-link)"
          fillOpacity={0.13}
          stroke="var(--color-link)"
          strokeWidth={1.75}
          strokeLinejoin="round"
        />
        {data.map((d, i) => {
          const [x, y] = points[i]!;
          return (
            <g key={d.name} onMouseEnter={() => setHover(i)}>
              <circle cx={x} cy={y} r={12} fill="transparent" />
              <circle
                cx={x}
                cy={y}
                r={hover === i ? 4.5 : 3.5}
                fill="var(--color-surface)"
                stroke="var(--color-link)"
                strokeWidth={2}
              />
            </g>
          );
        })}
        {data.map((d, i) => (
          <AxisLabel key={d.name} i={i} n={n} point={d} />
        ))}
      </svg>
      {shown && (
        <div
          role="tooltip"
          className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-[calc(100%+10px)] rounded-[7px]
                     border border-input bg-surface px-2.5 py-1.5 text-xs whitespace-nowrap shadow-sm"
          style={{ left: `${(shown.p[0] / W) * 100}%`, top: `${(shown.p[1] / H) * 100}%` }}
        >
          <div className="font-semibold text-ink">{shown.d.name}</div>
          <div className="text-body">
            Score <span className="font-mono">{formatScore(shown.d.value)}</span>
            {shown.d.pass != null && (
              <>
                {" "}
                · pass <span className="font-mono">{formatPercent(shown.d.pass)}</span>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
